/**
 * Generic address entry field used for adding a new address
 * Wrap all the address entry fields in a <div class="row"/> when using the grid
 * - Address fields are generic because they vary based on the user's locale
 * - Shows a state list dropdown if it exists
 * - Validates the postcode (zipcode) and shows the error message (vx_form-control-message)
 * - If it's a US address, show the state and zipcode on the same line
 */

import React from 'react';
import classNames from 'classnames';
import { DropdownMenu, TextInput } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import { stateListPropType } from 'prop-validators';
import _map from 'lodash/map';
import * as FPT from '../utility/fpt';

/**
 * State dropdown list
 */
class StateList extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    stateList: stateListPropType,
    required: PropTypes.bool,
    value: PropTypes.string, // default value
    errorMessage: PropTypes.string,
    handleChangeField: PropTypes.func.isRequired,
    handleFocus: PropTypes.func,
    handleBlur: PropTypes.func,
  };

  state = {
    selectedIndex: this.props.stateList.findIndex(
      (state) => state.$id === this.props.value
    ),
  };

  handleChangeOption = (index) => {
    this.setState({ selectedIndex: index });
    let value = this.props.stateList[index].$id;
    this.props.handleChangeField(this.props.fieldName, value);
  };

  getStateOptions = (stateList) => {
    return _map(stateList, (state) => {
      return {
        primaryText: state.$elt,
      };
    });
  };

  render() {
    let {
      id,
      fieldName,
      required,
      stateList = [],
      errorMessage,
      label,
    } = this.props;

    return (
      <div
        className={classNames({
          'ppvx_col-sm-12': this.props.country !== 'US', // eslint-disable-line
          'ppvx_col-sm-8': this.props.country === 'US', // eslint-disable-line
        })}
        style={{ margin: '10px 0 0', position: 'relative' }}
      >
        <div style={{ position: 'relative', marginTop: 0 }}>
          <DropdownMenu
            type="button"
            label={label}
            id={id}
            placeholder=""
            name={fieldName}
            required={required}
            selectedItemIndex={this.state.selectedIndex}
            onSelection={this.handleChangeOption}
            onFocus={this.props.handleFocus}
            options={this.getStateOptions(stateList)}
            errorText={errorMessage}
          />
        </div>
      </div>
    );
  }
}

/**
 * A generic address entry field that handles different fields specified by griffin
 * Values will be stored in component state
 */
class AddressEntryField extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    validationPattern: PropTypes.string,
    labels: PropTypes.object.isRequired, // addressEntryFormat.strings
    value: PropTypes.string, // default value
    errorMessage: PropTypes.string,
    handleChangeField: PropTypes.func.isRequired,
    handleFocus: PropTypes.func,
    handleBlur: PropTypes.func,
    handlePostcodeHasError: PropTypes.func.isRequired,
  };

  static defaultProps = {
    handleFocus: () => {},
    handleBlur: () => {},
  };

  state = {
    value: '',
    zipErrorMessage: '',
  };

  handleFocus = (value) => {
    this.props.handleFocus(this.props.fieldName, value);
  };

  /**
   * Validate postcode onBlur
   * - set the state with the error message from the labels object
   * - call parent's handlePostcodeHasError to disable submission of the form
   */
  handleBlur = (value) => {
    let shouldValidatePostcode =
      this.props.fieldName === 'postcode' && this.state.value;

    // if the postcode is invalid based on the validationPattern,
    // set zipErrorMessage = labels.validationError that we get from Griffin
    if (
      shouldValidatePostcode &&
      !new RegExp(`^${this.props.validationPattern}$`).test(this.state.value)
    ) {
      this.setState({ zipErrorMessage: this.props.labels.validationError });
      FPT.trackFieldError(
        'postcode',
        'INVALID_BILLING_ADDRESS_POSTCODE',
        this.props.labels.validationError
      );
      this.props.handlePostcodeHasError(true);
    } else {
      this.setState({ zipErrorMessage: '' });
      this.props.handlePostcodeHasError(false);
    }

    this.props.handleBlur(this.props.fieldName, value);
  };

  /**
   * Handle changing the input value
   * - call parent's handleChangeField to store the address value
   * - set the state so the component also keeps track of this value
   * @param {String} value - the input value
   */
  handleChangeField = (value) => {
    this.props.handleChangeField(this.props.fieldName, value);
    this.setState({ value });
    return value;
  };

  render() {
    let {
      id,
      country,
      fieldName,
      required,
      maxLength,
      validationPattern,
      labels,
      errorMessage,
      handleChangeField,
    } = this.props;
    let { optionalLabel, stateList } = labels;
    optionalLabel = required ? '' : ` ${optionalLabel || ''}`;
    let label = `${labels[`${fieldName}Label`]}${optionalLabel}`;
    let attributes = {
      required,
      maxLength: maxLength,
      pattern: validationPattern,
    };

    // if there is a stateList, let's render a <StateList/> dropdown
    if (this.props.fieldName === 'state' && stateList && stateList.length) {
      return (
        <StateList
          id={id}
          country={country}
          fieldName={fieldName}
          label={label}
          value={this.props.value}
          {...attributes}
          stateList={stateList}
          errorMessage={errorMessage}
          handleChangeField={handleChangeField}
          handleFocus={this.handleFocus}
        />
      );
    }

    let { zipErrorMessage } = this.state;

    return (
      <div
        className={classNames({
          'ppvx_col-sm-12':
            this.props.country !== 'US' || fieldName !== 'postcode',
          'ppvx_col-sm-4':
            ['US'].indexOf(this.props.country) >= 0 && fieldName === 'postcode',
        })}
        style={{ margin: '10px 0 0' }}
      >
        <TextInput
          id={id}
          name={`${fieldName}`}
          label={label}
          value={this.props.value}
          {...attributes}
          onFocus={(event) => this.handleFocus(event.target.value)}
          onBlur={(event) => this.handleBlur(event.target.value)}
          onChange={(event) => this.handleChangeField(event.target.value)}
          errorText={errorMessage || zipErrorMessage}
        />
      </div>
    );
  }
}

export default AddressEntryField;
